import {
  boolean,
  check,
  forward,
  type InferOutput,
  nullable,
  number,
  object,
  partialCheck,
  picklist,
  pipe,
  string
} from 'valibot'

import { VALIDATION_MESSAGES } from '@/constants/common/validation.constants'
import { PRICING_MODE } from '@/features/shipment-orders/models'
import { type ToursChargeAdditionalChargeExEntity } from '@/libs/api'

const RoundTypePicklist = picklist(['ROUND_UP', 'ROUND_DOWN', 'ROUND_HALF_UP'])

/**
 * 金額入力方式 (VOC_PJ-29/431 #3822)。
 * (NONE=該当なしは #3814 で enum ごと削除済み) Tour は AMOUNT/INCLUDED のみのため、
 * 配送依頼側 (`PRICING_MODE`) と異なり AMOUNT / INCLUDED の 2 値のみ。
 */
const TourPricingModePicklist = picklist([
  PRICING_MODE.AMOUNT,
  PRICING_MODE.INCLUDED
])

/**
 * 運行実績の追加請求料金のフォームスキーマを生成する。
 *
 * VOC_PJ-941 #3984: 金額が入力された AMOUNT 行の税区分・端数処理の必須チェックは
 * `requireTaxSetting` で制御する（定義は 1 箇所、判定だけ生成時の値を参照）。
 * - 詳細編集の通常時: true（ブランクにした時点でフィールド脇に必須エラー）
 * - キャンセル編集: false（金額 0 初期化で送るため必須にしない = BE ガードの isCanceling 除外と対応）
 * - 一括編集: false（UI に税設定欄がないため。BE ガードが最終防波堤）
 */
export const buildToursChargeAdditionalChargeFormValueSchema = ({
  requireTaxSetting
}: {
  requireTaxSetting: boolean
}) =>
  pipe(
    object({
      /** ReactHookFormで利用する際の判別用ID */
      uniqueId: string(),
      /** 運行実績明細ID */
      id: nullable(number()),
      /** 金額入力方式 (VOC_PJ-29/431 #3822)。INCLUDED=運賃込み（amount は null） */
      pricingMode: TourPricingModePicklist,
      /** 追加請求料金。INCLUDED（運賃込み）のとき null */
      amount: nullable(number(VALIDATION_MESSAGES.INPUT_AMOUNT)),
      /** 備考 */
      note: nullable(string()),
      /** 追加請求料金種別ID */
      additionalPaymentCategoryId: pipe(
        nullable(number()),
        check((chargeId) => chargeId !== null, VALIDATION_MESSAGES.SELECT_TYPE)
      ),
      /**
       * 税区分マスタ ID（B案カスケード、案件個別オーバーライド）。
       * VOC_PJ-462 L6 で追加。`null` のときはマスタ側 (`AdditionalPaymentCategory.taxCategoryId`) を参照。
       */
      taxCategoryId: nullable(number()),
      /**
       * 内税フラグ (VOC_PJ-844 #3624)。`null` ならカスケード fallback (個別 > APC マスタ)。
       * `=== true` のときのみ内税、null・false は外税。
       */
      isTaxInclusive: nullable(boolean()),
      /** 端数処理（案件個別オーバーライド） */
      roundType: nullable(RoundTypePicklist)
    }),
    // #3890 (VOC_PJ-882): AMOUNT + amount=null は「金額は後で入れる（保留）」として許容する。
    // INCLUDED + null（運賃込み）とは pricingMode で区別される。
    // #3822: INCLUDED（運賃込み）のとき amount は null
    forward(
      partialCheck(
        [['pricingMode'], ['amount']],
        ({ pricingMode, amount }) =>
          pricingMode === PRICING_MODE.AMOUNT || amount == null,
        VALIDATION_MESSAGES.INPUT_AMOUNT
      ),
      ['amount']
    ),
    // VOC_PJ-941 #3984 → #4556: AMOUNT 行の税区分・端数処理は金額の有無に関わらず必須。
    // INCLUDED（運賃込み）行は税設定欄自体が非表示のため対象外。内税フラグは対象外。
    forward(
      partialCheck(
        [['pricingMode'], ['taxCategoryId']],
        ({ pricingMode, taxCategoryId }) =>
          !requireTaxSetting ||
          pricingMode !== PRICING_MODE.AMOUNT ||
          taxCategoryId != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['taxCategoryId']
    ),
    forward(
      partialCheck(
        [['pricingMode'], ['roundType']],
        ({ pricingMode, roundType }) =>
          !requireTaxSetting ||
          pricingMode !== PRICING_MODE.AMOUNT ||
          roundType != null,
        VALIDATION_MESSAGES.REQUIRED_ITEM
      ),
      ['roundType']
    )
  )

/** 運行実績の追加請求料金のフォームスキーマ（税設定の必須チェックなし版。一括編集で使用） */
export const ToursChargeAdditionalChargeFormValueSchema =
  buildToursChargeAdditionalChargeFormValueSchema({
    requireTaxSetting: false
  })

/** 運行実績の追加請求料金のフォーム値 */
export type ToursChargeAdditionalChargeFormValue = InferOutput<
  typeof ToursChargeAdditionalChargeFormValueSchema
>

/**
 * #3822: aspida 生成型に対し pricingMode / amount を局所的に上書きする交差型。
 * - amount: INCLUDED のとき null になるため null 許容へ拡張
 * - pricingMode: 省略可かつ廃止済み 'NONE'（#3814）の旧データも受ける（AMOUNT へ倒す防御のため）
 * aspida 再生成後は pricingMode が必須 'AMOUNT'|'INCLUDED' になるため Omit で外してから再定義する。
 */
type ToursChargeAdditionalChargeEntityWithPricingMode = Omit<
  ToursChargeAdditionalChargeExEntity,
  'name' | 'amount' | 'pricingMode'
> & {
  /** 追加請求金額（INCLUDED のとき null） */
  amount: number | null
  /** 金額入力方式（省略時 AMOUNT。'NONE' は #3814 廃止済みの旧データ互換受け） */
  pricingMode?: 'AMOUNT' | 'INCLUDED' | 'NONE'
}

/** 運行実績Entityを行単位のフォーム値に変換する */
export const mapToToursChargeAdditionalChargeFormValue = ({
  id,
  amount,
  note,
  additionalPaymentCategoryId,
  taxCategoryId,
  isTaxInclusive,
  roundType,
  pricingMode
}: ToursChargeAdditionalChargeEntityWithPricingMode): ToursChargeAdditionalChargeFormValue => {
  // #3822: INCLUDED 以外 (廃止済み NONE 等の旧値含む) は AMOUNT に倒す防御
  const isIncluded = pricingMode === PRICING_MODE.INCLUDED
  return {
    uniqueId: String(id),
    id,
    pricingMode: isIncluded ? PRICING_MODE.INCLUDED : PRICING_MODE.AMOUNT,
    // INCLUDED（運賃込み）は金額を持たない。
    // AMOUNT + null は「金額は後で入れる（保留）」(#3890) のため null を保持する。
    // VOC_PJ-941 #3984: 内税明細の税込復元は BE 応答の出口 (CF-4) で行われるため、
    // amount には内税なら税込復元済みの値が入っている。
    amount: isIncluded ? null : amount,
    note,
    additionalPaymentCategoryId,
    taxCategoryId: taxCategoryId ?? null,
    // 内税フラグ (VOC_PJ-844 #3624)。null=上書きなし
    isTaxInclusive: isTaxInclusive ?? null,
    roundType: roundType ?? null
  }
}

/** 新規追加時の運行実績の追加請求料金のフォーム値を生成 */
export const createAddedTourChargeAdditionalChargeFormValue =
  (): ToursChargeAdditionalChargeFormValue => ({
    // 追加の場合はprefixをつけた現在時刻（ミリ秒）をユニークIDとして設定
    uniqueId: String(`added_${new Date().getTime()}`),
    id: null,
    pricingMode: PRICING_MODE.AMOUNT,
    amount: 0,
    note: null,
    additionalPaymentCategoryId: null,
    taxCategoryId: null,
    // 内税フラグ (VOC_PJ-844 #3624)。新規行は未設定 (APC 選択時に pre-fill)
    isTaxInclusive: null,
    roundType: null
  })
